import React, { useState } from 'react';
import { Btn, Modal, Textarea } from '../UI';
import { LIFECYCLE, LINE_STATUS, TAKEOFF_STATUS } from './mtoUtils';

export const Pill = ({ meta, children, style }) => (
  <span style={{
    display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999, whiteSpace: 'nowrap',
    background: meta?.bg || '#f1f5f9', color: meta?.color || '#334155', border: `1px solid ${(meta?.color || '#334155')}33`, ...style,
  }}>{children ?? meta?.label}</span>
);

export const TakeoffStatusPill = ({ status }) => <Pill meta={TAKEOFF_STATUS[status] || { label: status }} />;
export const LineStatusPill = ({ status }) => <Pill meta={LINE_STATUS[status] || { label: status }} style={{ fontSize: 10.5, padding: '2px 8px' }} />;

// Take-Off → … → Site Issue, with the stages this take-off has reached filled in
export const LifecycleBar = ({ status, hasMaterialRequest }) => {
  const reached = hasMaterialRequest ? 3 : status === 'approved' || status === 'superseded' ? 2 : status === 'pending_approval' ? 1 : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, overflowX: 'auto', padding: '2px 0 6px' }}>
      {LIFECYCLE.map((stage, i) => {
        const done = i < reached;
        const current = i === reached;
        return (
          <React.Fragment key={stage}>
            <span style={{
              fontSize: 11.5, fontWeight: current ? 800 : 600, padding: '5px 12px', borderRadius: 8, whiteSpace: 'nowrap',
              background: current ? '#0b318b' : done ? '#dcfce7' : 'var(--bg-base)',
              color: current ? '#fff' : done ? '#166534' : 'var(--text-secondary)',
              border: `1px solid ${current ? '#0b318b' : done ? '#86efac' : 'var(--border-light)'}`,
            }}>{stage}</span>
            {i < LIFECYCLE.length - 1 && <span style={{ color: '#94a3b8', fontSize: 12 }}>→</span>}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export const StatTile = ({ label, value, accent = '#0b318b', onClick, active }) => (
  <div onClick={onClick} style={{
    background: 'var(--bg-card)', border: active ? `2px solid ${accent}` : '1px solid black', borderTop: `3px solid ${accent}`,
    borderRadius: 10, padding: '12px 14px', cursor: onClick ? 'pointer' : 'default', minWidth: 0,
  }}>
    <div style={{ fontSize: 24, fontWeight: 800, color: accent, lineHeight: 1.1 }}>{value ?? 0}</div>
    <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 4 }}>{label}</div>
  </div>
);

// A short form in the app's standard modal: a comment / reason, then confirm.
export function CommentModal({ title, message, label = 'Comment', required, confirmLabel = 'Confirm', danger, onConfirm, onClose }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const blocked = required && !text.trim();
  const go = async () => {
    setBusy(true);
    try { await onConfirm(text.trim()); } finally { setBusy(false); }
  };
  return (
    <Modal title={title} onClose={onClose} width={500} blur={2}>
      {message && <p style={{ margin: '2px 2px 14px', fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{message}</p>}
      <Textarea label={`${label}${required ? ' *' : ''}`} value={text} onChange={setText} rows={4} />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
        <Btn variant="secondary" onClick={onClose} disabled={busy}>Cancel</Btn>
        <Btn onClick={go} loading={busy} disabled={blocked}
             style={danger ? { background: '#dc2626', color: '#fff', border: '1px solid #dc2626', boxShadow: 'none' } : undefined}>{confirmLabel}</Btn>
      </div>
    </Modal>
  );
}

export const Panel = ({ title, right, children, style }) => (
  <div style={{ background: 'var(--bg-card)', border: '1px solid black', borderRadius: 12, padding: 16, marginBottom: 16, ...style }}>
    {(title || right) && (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        {title && <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-primary)' }}>{title}</div>}
        {right}
      </div>
    )}
    {children}
  </div>
);

export const Field = ({ label, children }) => (
  <div style={{ border: '1px solid var(--border-light)', borderRadius: 8, padding: '8px 10px', minWidth: 0, background: 'var(--bg-base)' }}>
    <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>{label}</div>
    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginTop: 3, overflowWrap: 'anywhere' }}>{children || '—'}</div>
  </div>
);
